import { CeleryStatusesMapIdType } from "@shared/constants";
import CeleryJobRepository from "./CeleryJobRepository";

export type GetTaskFromJobIdReturnType<T = string> = {
  id: string;
  data: T;
  state: CeleryStatusesMapIdType;
};

/**
 * A type representing an endpoint that uses a queue to perform it's tasks.
 *
 * When an endpoint produces a task for the queue, it responds with a `job_id` which
 * allows for us to poll the backend using another endpoint to get the status of the task.
 */
export type TaskedEndpointResponse = {
  job_id: "string";
};

type JobTaskError = {
  id: string;
  data: { detail: string };
  state: CeleryStatusesMapIdType;
};

export function isJobTaskError(object: unknown): object is JobTaskError {
  if (typeof object !== "object" || object === null) {
    return false;
  }

  const { id, data, state } = object as JobTaskError;

  if (typeof data !== "object") {
    return false;
  }

  const { detail } = data;

  return detail !== undefined && id !== undefined && state !== undefined;
}

export const getTaskFromJobId = async <T = string>(
  taskId: string
): Promise<GetTaskFromJobIdReturnType<T>> => {
  const data = await new CeleryJobRepository().retrieve(taskId);

  return {
    id: data.id,
    data: data.data as T,
    state: data.state,
  };
};

/**
 * Extracts a URL from a task response.
 * The task data can be either a direct URL string or an object with { url: string }.
 * Returns undefined if the URL cannot be extracted.
 */
export function extractUrlFromTaskResponse(
  taskResponse: GetTaskFromJobIdReturnType<unknown>
): string | undefined {
  const { data } = taskResponse;

  // If data is a string, return it directly
  if (typeof data === "string" && data) {
    return data;
  }

  // If data is an object with a url property, extract it
  if (typeof data === "object" && data !== null && "url" in data) {
    const { url } = data as { url: unknown };
    if (typeof url === "string" && url) {
      return url;
    }
  }

  return undefined;
}
